import { useTranslation } from 'react-i18next'
import useConfirmPayment from '../../services/subscription/useConfirmPayment'
import useLimitedMode from '../../services/subscription/useLimitedMode'
import Banner from '../UI/Banner'

/**
 * The notice an unactivated account carries on every screen. It used to live
 * on Plan & Billing alone, which only worked because the app redirected
 * everyone there; now that nobody is redirected, the notice has to travel with
 * them and explain why everything is read-only.
 *
 * `Layout` renders it directly under the header, above the sidebar/content
 * row, so the bar spans the full width of the app. It sat inside
 * `ContentSection` for a while to dodge a second scrollbar, but that was the
 * sidebar's `min-h-full` resolving against a hardcoded row height, not the
 * banner's doing - fixing that there let the bar come back out here, full
 * width, and stopped it from colliding with the search box that page chrome
 * pins to the top-right corner of the content area.
 *
 * Confirm Payment is offered only for a bank transfer, matching the backend
 * guard on notify-payment.
 */
const LimitedModeBanner = () => {
  const { t } = useTranslation()
  const { isLimited, subscription } = useLimitedMode()
  const { mutate: confirmPayment, isLoading: isConfirming } = useConfirmPayment()

  if (!isLimited || !subscription) return null

  const canConfirmPayment =
    subscription.provider === 'bank_transfer' &&
    ['pending', 'past_due', 'unpaid'].includes(subscription.status)

  return (
    <Banner
      variant={canConfirmPayment ? 'action' : 'info'}
      text={t('accountNotActivatedBanner')}
      actionText={t('confirmPayment')}
      isActionDisabled={isConfirming}
      onAction={() => confirmPayment({ subscriptionId: subscription.id })}
    />
  )
}

export default LimitedModeBanner
